// Content for the "Claude for Professionals" live workshop delivered to
// Civils2000. Sourced from the original PowerPoint deck at
// "3. Clients/Civills 2000/Civils2000_Claude-for-Professionals Training_July26.pptx"
// — text transcribed faithfully, redesigned for the web presentation system
// (see SlideRenderer.js) rather than the original freeform pptx layout.

import { OKIRU_PRESENTATION_TEMPLATES } from '../okiruPresentationTemplate';

export const manifest = {
  id: 'civils2000-claude-professionals',
  title: 'Claude for Professionals',
  subtitle: 'Okiru | Full-Day Workshop',
  clientName: '',
  templateId: 'okiru-light',
  templateVersion: 3,
  logo: '/okiru-logo.png',
  logoDark: '/okiru-logo-white.png',
  footerLogoBlack: '/okiru-footer-black.png',
  footerLogoWhite: '/okiru-footer-white.png',
  partnerLogo: '/presentations/civils2000-claude-professionals/assets/claude-mark.png',
  clientLogo: '',
  theme: { ...OKIRU_PRESENTATION_TEMPLATES['okiru-light'].theme },
  modules: [
    { index: 1, name: 'AI foundations',               start: '08:45', durationMin: 75, firstSlide: 8 },
    { index: 2, name: 'Setup and orientation',         start: '10:00', durationMin: 45, firstSlide: 23 },
    { index: 3, name: 'Chat mastery',                  start: '11:00', durationMin: 45, firstSlide: 34 },
    { index: 4, name: 'Artifacts',                     start: '11:45', durationMin: 45, firstSlide: 40 },
    { index: 5, name: 'Projects and memory',           start: '12:30', durationMin: 30, firstSlide: 46 },
    { index: 6, name: 'Connectors, skills & plugins',  start: '13:45', durationMin: 45, firstSlide: 49 },
    { index: 7, name: 'Cowork',                        start: '14:30', durationMin: 60, firstSlide: 53 },
  ],
};

const ASSET_ROOT = '/presentations/civils2000-claude-professionals/assets';

export const slides = [
  {
    id: 1, layout: 'title',
    kicker: 'Okiru · Full-day workshop',
    title: 'Claude for Professionals',
    lead: 'One day. Your own laptop, your own account, your own work. You leave with something you will use on Monday morning.',
    meta: ['On-site workshop', 'Claude Team plan', '08:30 | 18:20'],
  },
  {
    id: 2, layout: 'statement',
    text: 'By close of day, every person in this room has built something real in their own Claude account, not watched a demo.',
    footnote: 'That is the only measure of success we are using.',
  },
  {
    id: 3, layout: 'agenda',
    kicker: 'Agenda', title: 'The day, module by module',
    columns: [
      [
        { time: '08:30', label: 'Welcome, objectives, agenda' },
        { time: '08:45', label: '1 · AI Foundations' },
        { time: '10:00', label: '2 · Claude setup & orientation' },
        { time: '10:45', label: 'Break' },
        { time: '11:00', label: '3 · Chat mastery' },
        { time: '11:45', label: '4 · Artifacts' },
        { time: '12:30', label: '5 · Projects & memory' },
        { time: '13:00', label: 'Lunch' },
      ],
      [
        { time: '13:45', label: '6 · Connectors, Skills & Plugins' },
        { time: '14:30', label: '7 · Cowork deep dive' },
        { time: '15:30', label: 'Break' },
        { time: '15:45', label: '8 · Excel & PowerPoint' },
        { time: '16:30', label: '9 · Report writing in Word' },
        { time: '17:00', label: '10 · Email writing' },
        { time: '17:20', label: '11 · Build your own sprint' },
        { time: '18:00', label: '12 · Wrap-up & next steps' },
      ],
    ],
  },
  {
    id: 4, layout: 'cards',
    kicker: 'House rules', title: 'How today works',
    lead: 'Mixed experience in this room is expected and completely fine. Nobody here is behind.',
    columns: 2,
    cards: [
      { title: 'There are no stupid questions', description: 'If something is unclear, say so. Six other people are wondering the same thing and will not ask.' },
      { title: 'Follow along on your laptop', description: 'No slide today is just for watching. If a screen is up, yours should be too.' },
      { title: 'We take regular breaks', description: 'Two breaks and a lunch. Step out sooner if you need to, nothing here is a lecture.' },
      { title: 'Ask for help the moment you are stuck', description: 'Raise a hand. Do not sit quietly through an exercise you cannot start.' },
      { title: 'Help each other', description: 'If you finish an exercise early, turn to the person next to you. Teaching it is how you keep it.' },
      { title: 'Use your own real work', description: 'Every exercise runs on a task from your own work. Sample data is provided, never live client data.' },
      { title: 'Before we finish, we appoint AI champions', description: 'Two or three power users per team, the people others go to first. Volunteer during the day, or be volunteered at the end.' },
    ],
  },
  {
    id: 5, layout: 'about',
    kicker: 'About Okiru', title: 'Smart data. Smarter transformation.',
    lead: 'Okiru Consulting is a Johannesburg-based transformation and compliance consultancy. We handle B-BBEE, ESG, employment equity and skills development for South African businesses, and we build the AI tooling that makes that work faster and better evidenced.',
    stats: [
      { value: 'Level 2', label: 'B-BBEE contributor', sub: '125% procurement recognition' },
      { value: '100%', label: 'Audit success rate', sub: 'Okiru-managed verifications' },
      { value: '51%', label: 'Black woman owned', sub: '51% black owned' },
      { value: 'POPIA & GDPR', label: 'Compliant', sub: 'Data handling built in' },
    ],
    tags: ['B-BBEE strategy', 'ESG & net zero', 'AI solutions', 'Employment equity', 'Skills development, WSP/ATR', 'Finance advisory', 'HR & human capital', 'Professional training'],
    contact: '12th Floor, 19 Ameshoff Street, Braamfontein, Johannesburg · contact@okiru.co.za · okiru.co.za',
    image: `${ASSET_ROOT}/okiru-teamwork.webp`, imageAlt: 'A professional team collaborating around a table',
  },
  {
    id: 6, layout: 'cards',
    kicker: 'While we are here', title: 'What else Okiru does for clients like you',
    lead: 'Each of these runs as advisory, as training, or as both, the same team, the same standard of evidence.',
    columns: 4,
    cards: [
      { title: 'B-BBEE', description: 'Scorecard strategy, verification readiness, sector codes, ownership structuring.', tag: 'Advisory + scorecard training' },
      { title: 'ESG & net zero', description: 'Materiality, GRI/TCFD reporting, decarbonisation planning, social impact.', tag: 'Advisory + ESG training' },
      { title: 'AI solutions', description: 'AI tools and dashboards that automate compliance data capture and reporting.', tag: 'Build + the training you are in' },
      { title: 'Employment Equity', description: 'EEA4 reporting, barrier analysis, workforce planning, committee support.', tag: 'Advisory + EE training' },
      { title: 'Skills development · WSP/ATR', description: 'WSP and ATR submissions, learnerships, bursary management, SETA liaison.', tag: 'Advisory + submission support' },
      { title: 'Finance for non-finance managers', description: 'Reading the numbers, budgets, margin and cash, for people who never trained in it.', tag: 'Training' },
      { title: 'Sales strategy', description: 'Pipeline discipline, proposal quality and commercial conversations that convert.', tag: 'Training' },
      { title: 'HR & human capital', description: 'HR strategy, policy, organisational design and change management.', tag: 'Advisory' },
    ],
    footer: 'Want any of these? Grab Chengetai at a break, or email contact@okiru.co.za.',
  },
  {
    id: 7, layout: 'profile',
    kicker: 'Your facilitator', name: 'Chengetai Myezwa', role: 'Okiru · Managing Director',
    bio: 'Chengetai founded and leads Okiru, a South African transformation and compliance consultancy specialising in B-BBEE, skills development and ESG advisory for financial services and ICT sector clients. He has led the firm’s shift toward AI-enabled delivery, building practical AI tools and training programmes that help compliance and HR teams work faster and more accurately.',
    specialisms: [
      'B-BBEE strategy and compliance, WSP/ATR, Employment Equity, Skills Development and learnership implementation',
      'ESG, net zero and sustainability advisory',
      'AI consulting, systems building, integrations and training',
    ],
    contacts: ['cmyezwa@okiru.co.za', 'okiru.co.za', 'linkedin.com/in/chengetai-myezwa-07089a67'],
    footnote: 'Okiru is a Johannesburg transformation and compliance company. Everything today comes from work we do daily, not a product tour.',
    photo: `${ASSET_ROOT}/facilitator-chengetai.jpg`,
  },
  {
    id: 8, layout: 'intro', module: 1,
    kicker: 'Module 01 · 08:45 · 75 min', title: 'AI foundations',
    lead: 'What this technology actually is, in plain language, and how to talk to it so it gives you something useful.',
    outcome: 'By the end you can explain, in one sentence each: what an LLM is, how prompting works, and where AI already touches your day.',
    image: `${ASSET_ROOT}/module-ai-foundations.webp`, imageAlt: 'A team reviewing analytics together',
  },
  {
    id: 9, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'What is AI, without the jargon',
    lead: 'Software that has read an enormous amount of writing and uses that to work out what a sensible next few words would be.',
    body: 'That is genuinely most of it. It is not a database you query and it is not a person who knows things. It is a very well-read pattern machine that you steer with instructions, which is why how you ask changes the answer so much.',
    image: `${ASSET_ROOT}/ai-without-jargon.png`, imageAlt: 'A digital human profile with a connected brain and icons representing chat, analysis, automation and AI',
    imageChrome: 'none',
  },
  {
    id: 10, layout: 'timeline', module: 1,
    kicker: 'Module 01', title: 'From rules to reasoning',
    lead: 'Nothing here happened overnight. Each step made the next one possible.',
    image: `${ASSET_ROOT}/ai-evolution-timeline.png`, imageAlt: 'The evolution from code and neural networks to conversational and agentic AI',
    steps: [
      { era: 'Until 2012', title: 'Rule-based software', description: 'A person writes every rule. If the situation was not anticipated, nothing happens.' },
      { era: '2012', title: 'Deep learning', description: 'Machines start learning patterns from examples instead of being told the rules.' },
      { era: '2018', title: 'Large language models', description: 'The same idea applied to language, at enormous scale.' },
      { era: '2022', title: 'Conversation', description: 'You stop learning software and start instructing it in plain English.' },
      { era: '2024 onward', title: 'Agentic AI', description: 'Tools that act, open files, use your apps, complete multi-step work.' },
    ],
  },
  {
    id: 11, layout: 'cards', module: 1,
    kicker: 'Module 01', title: 'You already use AI every day',
    lead: 'None of this felt like "using AI" at the time. Today is the same skill, pointed at your actual work.',
    columns: 3,
    cards: [
      { title: 'Spam filter', description: 'Decides what never reaches your inbox, learned from billions of emails.' },
      { title: 'Satnav routing', description: 'Predicts traffic you cannot see and reroutes you before you hit it.' },
      { title: 'Autocomplete', description: 'Guesses your next word. This is literally what a language model does.' },
      { title: 'Voice assistants', description: 'Turns speech into an instruction, then into an action.' },
      { title: 'Spellcheck & grammar', description: 'Suggests corrections based on how the language is normally used.' },
      { title: 'Recommendations', description: 'Streaming and shopping suggestions, drawn from patterns in behaviour.' },
    ],
  },
  {
    id: 12, layout: 'comparison', module: 1,
    kicker: 'Module 01', title: 'What actually changed between 2022 and today',
    columns: [
      { heading: 'Software before', items: ['You learn the software’s language, menus, fields, syntax.', 'One tool per job, and you move the work between them.', 'It answers a question you typed.'] },
      { heading: 'Software now', items: ['It learns yours. You describe the outcome in plain English.', 'One assistant that reaches into your files, mail and documents.', 'It completes a task you delegated, and shows its working.'] },
    ],
    callout: { label: 'The practical consequence for your team', text: 'The bottleneck moves from doing the admin to checking the work. That is a better place for a professional to spend the hour.' },
  },
  {
    id: 13, layout: 'keyterm', module: 1,
    kicker: 'Key term · 1 of 5', term: 'Prompt',
    definition: 'The instruction you give. Everything you type before you press send.',
    body: 'A prompt is not a search query. It is closer to a brief you would hand a new colleague: what to do, what to work from, who it is for.',
    image: `${ASSET_ROOT}/prompt-clear-brief.png`, imageAlt: 'A civil engineering professional giving a structured digital work brief on site',
    callouts: [
      { label: 'Everyday analogy', text: 'Briefing a subcontractor. "Fix it" gets you something. "Repair the 3m section of kerb on the north verge by Friday, photograph it before and after" gets you what you wanted.' },
      { label: 'At your organisation', text: 'Your prompt is where the WSP return, the site report, the client email actually gets defined.' },
    ],
  },
  {
    id: 14, layout: 'keyterm', module: 1,
    kicker: 'Key term · 2 of 5', term: 'Context window',
    definition: 'How much it can hold in mind at once, your files, plus everything said so far in this chat.',
    body: 'It is large, but not infinite. A very long, meandering chat eventually pushes the early detail out of view.',
    callouts: [
      { label: 'Everyday analogy', text: 'The desk in front of you. Everything spread out is available instantly; anything filed away in the cabinet is not, until you fetch it.' },
      { label: 'Practical rule', text: 'One chat per task. When the topic changes, start a new one, you will get sharper answers.' },
    ],
  },
  {
    id: 15, layout: 'keyterm', module: 1,
    kicker: 'Key term · 3 of 5', term: 'Token',
    definition: 'The unit it reads and writes in, roughly a short word or part of a word.',
    body: 'You will rarely need to think about tokens directly. It matters only because context limits and usage are measured in them.',
    callouts: [
      { label: 'Everyday analogy', text: 'Cubic metres on a delivery note. Nobody thinks in them while working, but the truck still has a capacity.' },
      { label: 'Rule of thumb', text: 'About 750 words ≈ 1,000 tokens. A 40-page report is well within reach.' },
    ],
  },
  {
    id: 16, layout: 'keyterm', module: 1,
    kicker: 'Key term · 4 of 5 · The important one', term: 'Hallucination',
    definition: 'When it states something confidently that is simply not true.',
    body: 'It happens most when you ask for facts it was never given, a specific rate, a clause number, a date. Give it the source and the risk drops sharply.',
    image: `${ASSET_ROOT}/ai-hallucination-verification.png`, imageAlt: 'A professional checking an AI-produced answer against source evidence',
    imageFocus: 'center bottom', imageZoom: 1.18,
    callouts: [
      { label: 'Everyday analogy', text: 'A confident colleague who would rather give you an answer than admit they are not sure. Useful, but you check the number before it goes to a client.' },
      { label: 'The rule for today', text: 'Claude drafts. A person signs. Nothing leaves the organisation unchecked.' },
    ],
  },
  {
    id: 17, layout: 'keyterm', module: 1,
    kicker: 'Key term · 5 of 5', term: 'Agent',
    definition: 'AI that does not just answer, it takes steps. Opens files, uses your tools, works through a task.',
    body: 'This is what Cowork is, and it is why we spend an hour on it this afternoon.',
    image: `${ASSET_ROOT}/ai-agent-workflow.png`, imageAlt: 'An AI agent moving through a supervised sequence of files, documents and business tools',
    imageFocus: 'center bottom', imageZoom: 1.18,
    callouts: [
      { label: 'Everyday analogy', text: 'The difference between asking someone how to file the site reports, and asking them to go and file the site reports.' },
      { label: 'Which is why', text: 'You give an agent access to one folder, never your whole machine. More on that in Module 7.' },
    ],
  },
  {
    id: 18, layout: 'numbered', module: 1,
    kicker: 'Module 01 · The core skill', title: 'The five habits of a strong prompt',
    lead: 'Learn these five and you have learned 90% of what today is about. Everything after this is which button to press.',
    image: `${ASSET_ROOT}/five-prompt-habits.png`, imageAlt: 'Five connected symbols representing specificity, context, structure, examples and refinement',
    imageFocus: 'center bottom', imageZoom: 1.18,
    items: [
      { title: 'Be specific', description: 'Say exactly what you want. "A 300-word summary", not "a summary".' },
      { title: 'Give context', description: 'Who is it for, why does it exist, what is the situation.' },
      { title: 'State the format', description: 'Table, bullet list, email, two paragraphs. Ask and you receive.' },
      { title: 'Give an example', description: 'For anything unusual, one sample of "good" beats a paragraph of description.' },
      { title: 'Refine, don’t restart', description: 'The first answer is a draft. Tell it what to change.' },
    ],
  },
  {
    id: 19, layout: 'worked-example', module: 1,
    kicker: 'Module 01 · Worked example', title: 'Same question, two prompts',
    weak: { label: 'Weak', prompt: 'Write me something about our WSP.', result: 'A generic explanation of what a Workplace Skills Plan is. Accurate, useless, and about somebody else’s company.' },
    strong: { label: 'Strong', prompt: 'You are writing for our skills development committee. Using the attached training register, draft the narrative section of our WSP submission: 400 words, three headings, training delivered, spend against target, plan for next cycle. UK English, plain and factual. Flag anything the register does not evidence.', result: 'A first draft you can edit, in your structure, from your data, with the gaps called out.' },
    footnote: 'The strong prompt uses all five habits: role and reader, source material, length and structure, tone, and an instruction to flag uncertainty rather than invent.',
  },
  {
    id: 20, layout: 'cards', module: 1,
    kicker: 'Module 01', title: 'Four mistakes everyone makes first',
    columns: 2,
    cards: [
      { title: 'Being vague and hoping', description: '"Improve this" gives it nothing to aim at. Say what better means to you.' },
      { title: 'Withholding the context', description: 'It cannot see your files, your client, or last week’s meeting unless you attach or explain them.' },
      { title: 'Accepting the first answer', description: 'Draft one is a starting point. Two rounds of "shorter and drop the jargon" usually gets you there.' },
      { title: 'Never saying who it is for', description: 'A note for a site foreman and a note for a client FD are different documents. Say which.' },
    ],
  },
  {
    id: 21, layout: 'exercise', module: 1,
    kicker: 'Exercise 1.1', meta: '15 min · in pairs', title: 'Turn a weak prompt into a strong one',
    steps: [
      { title: 'Open Claude and paste the weak prompt exactly as written', detail: '“Write me something about training.”' },
      { title: 'Read what comes back. Do not fix it yet.', detail: 'Notice how much of it you would have to throw away.' },
      { title: 'Rewrite it together using all five habits', detail: 'Pick a real task from one of your jobs. Specific · context · format · example · reader.' },
      { title: 'Run it in a new chat and compare the two side by side', detail: 'One pair will read both out to the room.' },
    ],
    scaffold: { label: 'Scaffold, fill in the blanks', text: 'You are writing for [who reads it]. Using [the attached / the notes below], write [what, how long] in [format: table / email / headings]. Tone: [plain, factual, UK English]. Flag anything you cannot evidence.', note: 'Keep this scaffold. It is on your cheat sheet, and it works for almost everything you will do today.' },
    checkFor: 'the second output needs editing, not rewriting, and you can point to which of the five habits fixed it.',
  },
  {
    id: '21b', layout: 'exercise-example', module: 1,
    kicker: 'Module 01 · Optional practice', meta: '5-10 min · solo', title: 'Summarising meeting notes into actions',
    scenario: 'You have just left a 45-minute site progress meeting with scribbled notes covering delays, budget concerns and who agreed to do what.',
    task: 'Turn those rough notes into a short, structured summary you could send to your project manager within five minutes of leaving the room.',
    examplePrompt: 'Review the following meeting notes and identify the five most important decisions, action items, responsible people and deadlines. Present the results in a structured table.\n\n[Paste your meeting notes here]',
    expectedResult: 'A table with columns for Decision/Action, Responsible Person and Deadline, pulling out only the five most important items rather than everything that was discussed.',
    tryItYourself: 'Add a fourth column for Priority (High/Medium/Low) and ask Claude to sort the table by priority instead of by the order things were raised.',
  },
  {
    id: 22, layout: 'cards', module: 1,
    kicker: 'Free tools from Okiru', title: 'Open these now, you will use them all day',
    lead: 'Two things we built to make the last two slides easier.',
    columns: 2,
    cards: [
      { title: 'Prompt Generator', description: 'Answer a few questions about the task and it writes the structured prompt for you, using the same five habits we just covered. Good training wheels for week one.', tag: 'okiru.biz' },
      { title: 'AI Tool Advisor', description: 'Describe what you are trying to get done and it points you at the right tool in about thirty seconds, rather than you trialling six of them.', tag: 'okiru.co.za/free-ai-toolkit' },
    ],
    footer: 'Bookmark the various free tools available to you.',
  },
  {
    id: 23, layout: 'intro', module: 2,
    kicker: 'Module 02 · 10:00 · 45 min', title: 'Setup and orientation',
    lead: 'Five minutes of setup now saves confusion in every module that follows.',
    outcome: 'By the end you are logged in, you know what your Team plan includes, and your preferences are set.',
    image: `${ASSET_ROOT}/module-setup.webp`, imageAlt: 'A facilitator leading a laptop workshop',
  },
  {
    id: 24, layout: 'cards', module: 2,
    kicker: 'Module 02', title: 'What your Claude Team seat means',
    lead: 'You are on your company’s shared organisation, not a personal account. Three practical consequences:',
    columns: 3,
    cards: [
      { title: 'Admin sets the tools', description: 'Connectors to Outlook and SharePoint are approved centrally by IT, not switched on ad hoc.' },
      { title: 'You can share your work', description: 'Artifacts and projects can be shared inside your organisation, a good tracker becomes everyone’s tracker.' },
      { title: 'Your chats stay yours', description: 'Colleagues do not browse your conversations. Sharing is a deliberate act.' },
    ],
  },
  {
    id: 25, layout: 'numbered', module: 2,
    kicker: 'Module 02 · Orientation', title: 'The window, labelled',
    shot: `${ASSET_ROOT}/claude-desktop-home.png`, caption: 'Claude for Desktop home screen: navigation on the left, composer in the centre',
    items: [
      { title: 'New chat', description: 'Start here for anything one-off.' },
      { title: 'Projects', description: 'Grouped work with saved context. Module 5.' },
      { title: 'Composer', description: 'Where you type, attach files and toggle tools.' },
      { title: 'Artifacts panel', description: 'Finished outputs live here. Module 4.' },
      { title: 'Customize & settings', description: 'Skills, connectors, plugins, style, memory.' },
    ],
  },
  {
    id: 26, layout: 'cards', module: 2,
    kicker: 'Module 02', title: 'One menu holds the three things that extend Claude',
    lead: 'Find Customize once and you have found Skills, Connectors and Plugins. We come back to all three in Module 6.',
    caption: 'Customize menu open, showing the Skills / Connectors / Plugins tabs', shot: 'customize-menu-tabs.png',
    columns: 3,
    cards: [
      { title: 'Skills', description: 'Instructions saved once, reused every time.' },
      { title: 'Connectors', description: 'Links to Outlook, SharePoint, Drive and similar.' },
      { title: 'Plugins', description: 'Bundles of skills and connectors for one kind of role.' },
    ],
  },
  {
    id: 27, layout: 'cards', module: 2,
    kicker: 'Module 02', title: 'Two settings worth two minutes',
    columns: 2,
    cards: [
      { title: 'Writing style', description: 'Set it once and stop asking for it in every prompt. Suggested: "Direct, UK English, plain language, no filler."', caption: 'Settings → writing style preference field, with example text entered', shot: 'settings-writing-style.png' },
      { title: 'Memory', description: 'Account-level recall of how you work. Open it, read what is there, delete anything irrelevant. You are always in control of it.', caption: 'Settings → memory panel, showing stored items and the delete control', shot: 'settings-memory-panel.png' },
    ],
  },
  {
    id: 28, layout: 'cards', module: 2,
    kicker: 'Module 02', title: 'Chat, Projects, Cowork, one sentence each',
    lead: 'If you remember nothing else from this module, remember which one to reach for.',
    columns: 3,
    cards: [
      { title: 'Chat', description: 'A single conversation about one thing.', tag: 'Reach for it when: you need an answer, a draft, or quick research.' },
      { title: 'Projects', description: 'A workspace that already knows your background material.', tag: 'Reach for it when: the work repeats, a client, a monthly report, a course.' },
      { title: 'Cowork', description: 'Claude working across a folder of real files, step by step.', tag: 'Reach for it when: the job is "sort this out", not "tell me about this".' },
    ],
  },
  {
    id: '29a', layout: 'walkthrough', module: 2,
    kicker: 'Exercise 2.1 · Claude for Desktop', step: 1, totalSteps: 4,
    title: 'Sign in with your work account',
    lead: 'Open Claude for Desktop and confirm you are working inside your company’s organisation.',
    instruction: 'Use your work email. When the home screen opens, check the organisation name in the bottom-left account menu.',
    tip: 'Do not continue in a personal account; Team features and approved connectors may be different.',
    shot: 'login-org-selected.png',
    caption: 'The Claude sign-in screen — continue with your work email.',
    screenshotPlaceholder: 'Capture the Claude account menu or sign-in confirmation with your company’s organisation selected.',
  },
  {
    id: '29b', layout: 'walkthrough', module: 2,
    kicker: 'Exercise 2.1 · Claude for Desktop', step: 2, totalSteps: 4,
    title: 'Open Customize',
    lead: 'This is where Claude groups Skills, Connectors and Plugins.',
    instruction: 'Select Customize in the left navigation, then identify the Skills, Connectors and Plugins sections. Do not switch anything on yet.',
    tip: 'The final capture should show the full Claude window with Customize selected and the three sections visible.',
    shot: 'customize-menu-tabs.png',
    caption: 'Customize open in Claude for Desktop, with Skills, Connectors and Plugins visible.',
    screenshotPlaceholder: 'Capture Claude for Desktop with Customize open and all three extension sections visible.',
  },
  {
    id: '29c', layout: 'walkthrough', module: 2,
    kicker: 'Exercise 2.1 · Claude for Desktop', step: 3, totalSteps: 4,
    title: 'Set your writing style',
    lead: 'Save the preference once so you do not repeat it in every prompt.',
    instruction: 'Open Settings, find the writing style preference, and save: “Direct, UK English, plain language, no filler.”',
    tip: 'The screenshot should include both the saved wording and the confirmation state.',
    shot: 'settings-writing-style.png',
    caption: 'Writing style preference saved in Claude settings.',
    screenshotPlaceholder: 'Capture the Claude writing-style setting with your organisation’s wording saved and confirmation visible.',
  },
  {
    id: '29d', layout: 'walkthrough', module: 2,
    kicker: 'Exercise 2.1 · Claude for Desktop', step: 4, totalSteps: 4,
    title: 'Review memory',
    lead: 'Know what Claude remembers and how to remove anything that should not remain.',
    instruction: 'Open Memory, read the stored items, and remove anything irrelevant or inappropriate for work use.',
    tip: 'Use a training-safe example in the screenshot; do not expose personal or client information.',
    shot: 'settings-memory-panel.png',
    caption: 'Claude memory panel with the review and delete controls visible.',
    screenshotPlaceholder: 'Capture the Claude memory panel with a safe example and the delete control clearly visible.',
  },
  {
    id: 30, layout: 'outcome', module: 2,
    kicker: 'Final outcome · Exercise 2.1', title: 'A properly set-up account looks like this',
    caption: 'Settings screen with the style preference saved and memory reviewed', shot: 'settings-final-state.png',
    checks: [
      'You are logged into your company’s organisation, not a personal account',
      'You can name which connectors are already enabled',
      'A writing style is saved and you have seen it take effect',
      'You have opened memory and know how to clear it',
    ],
    footnote: 'If a connector you need is missing, that is an IT approval, note it now and we will raise it together at lunch.',
  },
  {
    id: 31, layout: 'intro', module: 3,
    kicker: 'Module 03 · 11:00 · 45 min', title: 'Chat mastery',
    lead: 'Most people start in Chat, and stop far too early. This module is about the second and third message.',
    outcome: 'By the end you use web search and extended thinking deliberately, and you refine instead of restarting.',
    image: `${ASSET_ROOT}/module-chat.webp`, imageAlt: 'A facilitator leading a practical technology workshop',
  },
  {
    id: 32, layout: 'comparison', module: 3,
    kicker: 'Module 03', title: 'Web search: switch it on for anything current',
    lead: 'Without it, Claude answers from what it already knows. That is fine for explanation, and wrong for a rate that changed last month.',
    caption: 'Composer with the web search toggle visible and switched on', shot: 'composer-web-search-on.png',
    columns: [
      { heading: 'Switch it on', items: ['SETA and B-BBEE regulation changes', 'Current rates, thresholds, deadlines', 'Anything a client mentioned in the news', 'Competitor or tender activity'] },
      { heading: 'Leave it off', items: ['Drafting from a file you attached', 'Explaining a concept', 'Rewriting or shortening your own text', 'Anything confidential to your organisation'] },
    ],
    footnote: 'Always check the source it cites. Search reduces invention; it does not remove your responsibility to verify.',
  },
  {
    id: '32b', layout: 'exercise-example', module: 3,
    kicker: 'Module 03 · Optional practice', meta: '10-15 min · solo', title: 'Researching a compliance update before a client call',
    scenario: 'A client asks whether anything has changed with the B-BBEE Codes of Good Practice that could affect their scorecard this quarter, and you have twenty minutes before the call.',
    task: 'Use Claude with web search switched on to find and summarise any recent regulatory changes, with sources you can point to on the call.',
    examplePrompt: 'With web search on: what changes, if any, have been made to the B-BBEE Codes of Good Practice in South Africa in the last six months that would affect a mid-sized construction company’s scorecard? List each change with its source and the date it was published.',
    expectedResult: 'A short list of changes, or a clear statement that nothing has changed, each with a cited, datable source you could open and check before the call.',
    tryItYourself: 'Ask Claude to also flag anything that is still only proposed or under public comment, not yet finalised, so you do not tell the client something is confirmed when it is not.',
  },
  {
    id: 33, layout: 'comparison', module: 3,
    kicker: 'Module 03', title: 'Extended thinking: when it earns the extra wait',
    lead: 'It takes longer because it works the problem through before answering. Worth it when the problem has steps; wasted on a rewrite.',
    caption: 'Composer with the extended thinking toggle visible, plus a response showing its reasoning', shot: 'composer-extended-thinking.png',
    columns: [
      { heading: 'Worth it', items: ['Reconciling a training register against a WSP target.', 'Comparing three tender responses.', 'Working out why two spreadsheets disagree.'] },
      { heading: 'Not worth it', items: ['"Shorten this paragraph."', '"Turn these notes into bullets."', '"What does EEA4 stand for?"'] },
    ],
    callout: { label: 'The tell', text: 'If you would need a pen and paper to do it yourself, turn it on.' },
  },
  {
    id: 34, layout: 'cards', module: 3,
    kicker: 'Module 03 · The habit that matters most', title: 'Refine in the thread. Do not start again.',
    lead: 'Starting a new chat throws away everything it has learned about your task. Telling it what to change keeps all of that and fixes the one thing.',
    columns: 4, quoteStyle: true,
    cards: [
      { title: 'Length', description: '"Half the length. Keep the numbers."' },
      { title: 'Language', description: '"Drop the technical terms, a site foreman must follow it."' },
      { title: 'Format', description: '"Reformat as a table: item, owner, due date."' },
      { title: 'Challenge', description: '"Which of these claims is not supported by the file I gave you?"' },
    ],
    footer: 'That last one is the most valuable sentence in this deck. Ask it before anything goes to a client.',
  },
  {
    id: 35, layout: 'exercise', module: 3,
    kicker: 'Exercise 3.1', meta: '20 min · on your own', title: 'Research something current, then make it usable',
    steps: [
      { title: 'Turn web search on' },
      { title: 'Ask about a live compliance topic', detail: '“What has changed in South African skills development / SETA requirements in the last 12 months that affects an employer submitting a WSP? Cite your sources.”' },
      { title: 'Reformat it for a manager', detail: '“Rewrite that as a one-page briefing for a non-technical manager. Three headings, no acronyms without explaining them once.”' },
      { title: 'Make one more refinement', detail: 'Tone, length or format, your choice. Same thread.' },
      { title: 'Open one cited source and check it says what Claude claims' },
    ],
    checkFor: 'you are holding a one-page briefing you would actually send, and you have personally opened one of its sources.',
  },
  {
    id: 36, layout: 'outcome', module: 3,
    kicker: 'Final outcome · Exercise 3.1', title: 'A briefing you would actually send',
    caption: 'The finished one-page briefing, three headings, citations visible', shot: 'briefing-with-citations.png',
    checks: [
      'Three headings, one page, no unexplained acronyms',
      'Every claim carries a citation',
      'You opened one source and it said what Claude said',
      'You refined in the same thread, you never started over',
    ],
    footnote: 'If a citation did not check out, say so to the room. That is the most useful thing anyone can find this morning.',
  },
  {
    id: 37, layout: 'intro', module: 4,
    kicker: 'Module 04 · 11:45 · 45 min', title: 'Artifacts',
    lead: 'When the output is a thing you keep, a calculator, a checklist, a tracker, not a message in a chat.',
    outcome: 'By the end you have built one and refined it once.',
    image: `${ASSET_ROOT}/module-artifacts.webp`, imageAlt: 'A team building a data-driven client deliverable',
  },
  {
    id: 38, layout: 'lead', module: 4,
    kicker: 'Module 04', title: 'What an artifact is, in one sentence',
    lead: 'A finished, reusable output that sits beside the conversation, and that you can reopen and edit later.',
    body: 'A chat answer is something you read once. An artifact is something you use, a working cost calculator, a compliance checklist, a small dashboard. Same conversation, different kind of output.',
    image: `${ASSET_ROOT}/chat-to-artifact.png`, imageAlt: 'Information flowing into a polished reusable digital dashboard',
    imageFocus: 'center bottom', imageZoom: 1.12,
    callout: { label: 'Do not use one when', text: 'the answer is a one-off you will never edit or return to. A chat message is fine.' },
  },
  {
    id: 39, layout: 'cards', module: 4,
    kicker: 'Module 04', title: 'Two kinds: static and live',
    columns: 2,
    cards: [
      { title: 'Static', description: 'Built once from what you gave it. You edit it by asking for changes.', tag: 'Use for: a WSP checklist, a site inspection tracker, a training cost calculator.' },
      { title: 'Live', description: 'Refreshes from connected data rather than a fixed snapshot.', tag: 'Cowork desktop only, not in this exercise.' },
    ],
    footer: 'Start static. Almost everything useful at work, a checklist, a calculator, a tracker, works perfectly well as a static artifact you update when the inputs change.',
  },
  {
    id: 40, layout: 'cards', module: 4,
    kicker: 'Module 04', title: 'Three you could build before lunch',
    lead: 'Pick whichever is closest to your own job, you will build one in twenty minutes.',
    columns: 3,
    cards: [
      { title: 'Training cost calculator', description: 'Learners × programme × cost per head, with a total and a per-SETA-cycle view.' },
      { title: 'Compliance checklist tracker', description: 'Every WSP/ATR requirement, who owns it, what evidence exists, what is outstanding.' },
      { title: 'Site inspection tracker', description: 'Sites, last visit, findings, actions and due dates in one filterable view.' },
    ],
    pattern: { label: 'The prompt pattern for any artifact', text: 'Build me a [tracker / calculator / checklist] for [the job]. Inputs: [what I will type in]. It should show [what I need out]. Keep it simple enough to use on a phone on site.' },
  },
  {
    id: '40b', layout: 'exercise-example', module: 4,
    kicker: 'Module 04 · Optional practice', meta: '10-15 min · solo', title: 'Drafting a client progress update as an artifact',
    scenario: 'It is the first Friday of the month, and you owe three different clients a written progress update on their site works, each with different numbers and timelines.',
    task: 'Build a reusable progress-update template as an artifact, then fill it with one client’s real details so it becomes a document you send, not just a chat reply.',
    examplePrompt: 'Build me a client progress update template for a civil works site. It should include: project name, reporting period, work completed this period, work planned next period, any delays or risks, and a one-line overall status. Keep the tone professional and factual, suitable for a non-technical client contact.',
    expectedResult: 'An editable artifact with clearly labelled sections you can fill in per client, rather than a one-off paragraph you would have to rebuild from scratch next month.',
    tryItYourself: 'Ask Claude to add a short "Photos referenced" line to the template, then duplicate the artifact and fill it in for a second, different client.',
  },
  {
    id: 41, layout: 'exercise', module: 4,
    kicker: 'Exercise 4.1 · Where to click', meta: '20 min', title: 'Building your first artifact',
    steps: [
      { title: 'Describe what you need', detail: 'Use the pattern from the previous slide.', caption: 'Composer with the artifact prompt typed out', shot: 'composer-artifact-prompt.png' },
      { title: 'It appears beside the chat', detail: 'Try it. Put real numbers in.', caption: 'Artifacts panel open, showing the working calculator', shot: 'artifacts-panel-calculator.png' },
      { title: 'Ask for one change', detail: '“Add a column for the SETA cycle.”', caption: 'The same artifact after a refinement, a column added', shot: 'artifact-refined-column.png' },
    ],
    checkFor: 'you can close the chat, reopen the artifact tomorrow, and it still works with your numbers in it.',
  },
  {
    id: 42, layout: 'outcome', module: 4,
    kicker: 'Final outcome · Exercise 4.1', title: 'A tool you own, not a message you scroll past',
    caption: 'The finished artifact, filled in with real numbers from your work', shot: 'artifact-final-real-numbers.png',
    checks: [
      'It relates to a job you actually do',
      'You put your own numbers in and the output was right',
      'You refined it once and the change stuck',
      'You can find it again tomorrow',
    ],
    footnote: 'If yours would help a colleague, share it, on Team, a good artifact becomes everyone’s tool.',
  },
  {
    id: 43, layout: 'intro', module: 5,
    kicker: 'Module 05 · 12:30 · 30 min', title: 'Projects and memory',
    lead: 'Stop re-explaining your job every morning.',
    outcome: 'By the end you have a working Project for one recurring piece of your work.',
    image: `${ASSET_ROOT}/module-projects.webp`, imageAlt: 'A team collaborating on a recurring project',
  },
  {
    id: 44, layout: 'comparison', module: 5,
    kicker: 'Module 05', title: 'Projects vs memory, the practical difference',
    columns: [
      { heading: 'A Project', items: ['A folder for one body of work, with its own memory built from the chats inside it. Claude reads all of it as background, every time.', 'Scope: one client, one report type, one course.'] },
      { heading: 'Memory', items: ['Account-wide recall of how you work, your style, your role, the things you never want to retype.', 'Scope: everything you do, everywhere.'] },
    ],
    callout: { label: 'The rule', text: 'If you will come back to it, make it a Project. If it describes you rather than the work, it belongs in memory.' },
  },
  {
    id: '44b', layout: 'exercise-example', module: 5,
    kicker: 'Module 05 · Optional practice', meta: '10 min · solo', title: 'Preparing for tomorrow’s client meeting using Project knowledge',
    scenario: 'You have a Project already set up for one client’s site reports, and tomorrow morning you are meeting them to walk through progress since the last visit.',
    task: 'Ask Claude, inside that Project, to prepare you a one-page briefing using only what is already stored there, without re-explaining the background.',
    examplePrompt: 'Using everything in this Project, prepare a one-page briefing for tomorrow’s client meeting: what has changed since our last report, any outstanding actions from previous visits, and three questions I should ask the client. Do not invent anything that is not in the stored files.',
    expectedResult: 'A one-page briefing built entirely from your Project’s existing files and chat history, with no need to attach anything or explain the background again.',
    tryItYourself: 'Ask it to flag anything it could not find evidence for in the Project, so you know exactly what to double-check before walking in.',
  },
  {
    id: 45, layout: 'exercise', module: 5,
    kicker: 'Exercise 5.1 · Where to click', meta: '15 min', title: 'Create a Project for work you repeat',
    steps: [
      { title: 'New Project', detail: 'Name it after the work, not the month.', caption: 'Create-project dialog, name field filled with "Site reports"', shot: 'create-project-dialog.png' },
      { title: 'Add one reference file', detail: 'A past report, or a short style note.', caption: 'Project knowledge panel with a file being uploaded', shot: 'project-file-upload.png' },
      { title: 'Start a chat inside it', detail: 'Ask: "What format do our reports use?"', caption: 'A project chat answering with the reference file already in mind', shot: 'project-chat-answer.png' },
    ],
    checkFor: 'a fresh chat inside the Project answers correctly without you attaching or explaining anything.',
  },
  {
    id: 46, layout: 'intro', module: 6,
    kicker: 'Module 06 · 13:45 · 45 min', title: 'Connectors, skills and plugins',
    lead: 'Pipes, instructions and bundles. Three words, one afternoon.',
    outcome: 'By the end you have one connector confirmed, one skill installed and one skill you built yourself.',
    image: `${ASSET_ROOT}/module-connectors.webp`, imageAlt: 'Two colleagues reviewing connected work',
  },
  {
    id: 47, layout: 'cards', module: 6,
    kicker: 'Module 06', title: 'The three-layer model',
    lead: 'People confuse these three constantly. One analogy fixes it for good.',
    image: `${ASSET_ROOT}/connectors-skills-plugins.png`, imageAlt: 'Connected business capabilities arranged around the Okiru technology mark',
    imageFocus: 'center bottom', imageZoom: 1.06,
    columns: 3,
    cards: [
      { title: 'Connectors', tag: 'The pipes.', description: 'They link Claude to Outlook, SharePoint, Teams, Drive. Without one, Claude cannot see your mail or your documents.' },
      { title: 'Skills', tag: 'The instructions.', description: 'One job, done your way, every time. "Format a site report like ours" saved once instead of retyped weekly.' },
      { title: 'Plugins', tag: 'The bundles.', description: 'Several skills and connectors packaged for one kind of role, sales, HR, finance, so setup is one click, not ten.' },
    ],
    footer: 'Team-plan note: connectors that can change data (send mail, edit a file) may require approval per task, not just once. That is a safety setting, not a fault.',
  },
  {
    id: 48, layout: 'pattern', module: 6,
    kicker: 'Module 06', title: 'Building your own skill',
    lead: 'If you have typed the same instruction three times, it should be a skill. Look for the sentence you keep repeating.',
    fields: [
      { label: 'Name', value: 'Site report' },
      { label: 'When to use', value: 'any site inspection write-up' },
      { label: 'Do', value: 'Use our four headings, site, findings, actions, sign-off. UK English, plain, factual. Photos referenced by number. Flag anything the notes do not evidence.' },
    ],
    footnote: 'Name it after the job, describe when to use it, then write the instruction exactly as you would say it to a new starter.',
    caption: 'Custom skill creation screen with name, description and instructions filled in', shot: 'skill-creation-screen.png',
  },
  {
    id: '48b', layout: 'exercise-example', module: 6,
    kicker: 'Module 06 · Optional practice', meta: '10-15 min · solo', title: 'Turning a recurring report format into a skill',
    scenario: 'You have noticed you type almost the same instruction every time someone asks Claude to write up a site inspection: the same four headings, the same tone, the same rule about flagging unevidenced claims.',
    task: 'Instead of retyping that instruction again, capture it once as a skill so every future site report follows the same format automatically.',
    examplePrompt: 'Create a skill called "Site report". Use it whenever I ask for a site inspection write-up. Instructions: use four headings, Site, Findings, Actions, Sign-off. UK English, plain and factual. Reference photos by number. Flag anything the notes do not evidence.',
    expectedResult: 'A saved skill that, once installed, produces a correctly formatted site report from raw notes without you restating the format, tone or flagging rule each time.',
    tryItYourself: 'Run the same raw notes through Claude with the skill on and with it off, and compare how much you would have had to fix by hand in the second version.',
  },
  {
    id: 49, layout: 'exercise', module: 6,
    kicker: 'Exercise 6.1', meta: '20 min', title: 'One connector, one installed skill, one skill of your own',
    steps: [
      { title: 'Check the Microsoft 365 connector', detail: 'Now under Directory, Connectors tab. Enabled? Good. Not enabled? Write it down, we raise it with IT together, do not chase it now.', caption: 'Directory, Connectors tab, Microsoft 365 entry, enabled state visible', shot: 'connectors-directory-m365.png' },
      { title: 'Browse the Skills tab and install one', detail: 'Same screen, different tab. Pick something you would genuinely use this week.', caption: 'Directory, Skills tab, with the install button in view', shot: 'skills-directory-install.png' },
      { title: 'Build one skill from a task you repeat', detail: 'A report format, a standard sign-off, a way you always want figures presented.' },
      { title: 'Test it on something real', detail: 'If the output is not what you meant, edit the skill, not the prompt.' },
    ],
    checkFor: 'you run your own skill on a real task and the output comes back in your organisation’s format without you asking for it.',
  },
  {
    id: 50, layout: 'intro', module: 7,
    kicker: 'Module 07 · 14:30 · 60 min', title: 'Cowork',
    lead: 'Claude stops answering and starts doing, across a folder of your real files, one approved step at a time.',
    outcome: 'By the end you understand the folder security model and have run one multi-step task yourself.',
    image: `${ASSET_ROOT}/module-cowork.jpg`, imageAlt: 'Okiru automation engine graphic',
  },
  {
    id: 51, layout: 'comparison', module: 7,
    kicker: 'Module 07', title: 'What Cowork does that Chat does not',
    columns: [
      { heading: 'Chat', items: ['You paste in one file at a time.', 'It tells you what to do next.', 'One question, one answer.'] },
      { heading: 'Cowork', items: ['It works across a whole folder you have granted access to.', 'It does the next step and shows you the result.', 'A task with ten steps, run to completion with your approval at each one.'] },
    ],
    footnote: 'Realistic jobs: organising a folder of site reports, summarising a batch of learner applications, preparing first drafts of documents you produce every month.',
  },
  {
    id: 52, layout: 'comparison', module: 7,
    kicker: 'Read this one twice', title: 'One folder. Never your whole computer.',
    lead: 'Cowork can only see what you point it at. That boundary is the whole security model, and it is yours to set.',
    columns: [
      { heading: 'Do this', items: ['Make a working folder for the task', 'Copy in only the files that task needs', 'Grant access to that folder', 'Start in Manual mode'] },
      { heading: 'Never this', items: ['Granting your whole Documents folder', 'Granting the Desktop', 'Pointing it at a live client folder "just to try"', 'Auto mode on your first run'] },
    ],
    footnote: 'Today you will use the sample folder provided. Nothing on this course touches live client data.',
  },
  {
    id: 53, layout: 'cards', module: 7,
    kicker: 'Module 07', title: 'Manual mode vs Auto mode',
    caption: 'Cowork mode selector showing Manual and Auto, with Manual chosen', shot: 'cowork-mode-selector.png',
    columns: 2,
    cards: [
      { title: 'Manual', description: 'It asks before every action. You see each step and approve it.', tag: 'Use this today, and for the first month.' },
      { title: 'Auto', description: 'It proceeds and only stops for anything it flags as risky.', tag: 'Only once you trust the task and the folder.' },
    ],
    footer: 'Manual is slower and that is the point, you are learning what it decides to do. Watch the approvals today; you will know within an hour which tasks you would trust on Auto. A third option, Skip all approvals, exists too: do not use it today.',
  },
  {
    id: 54, layout: 'exercise', module: 7,
    kicker: 'Exercise 7.1 · Where to click', meta: '30 min · sample folder provided', title: 'Organise a messy folder, safely',
    steps: [
      { title: 'Open Cowork', caption: 'Cowork tab, empty state', shot: 'cowork-tab-empty.png' },
      { title: 'Grant the folder', caption: 'Folder access dialog with only the sample folder selected', shot: 'cowork-folder-access-dialog.png' },
      { title: 'Choose Manual', caption: 'Mode selector set to Manual', shot: 'cowork-mode-manual-set.png' },
      { title: 'Approve each step', caption: 'Live session mid-task with an approval prompt showing', shot: 'cowork-approval-prompt.png' },
    ],
    scaffold: { label: 'The task', text: 'Organise the files in this folder by document type into subfolders. Do not delete anything. Then write me a one-paragraph summary of what the folder contains.' },
  },
  {
    id: 55, layout: 'outcome', module: 7,
    kicker: 'Final outcome · Exercise 7.1', title: 'A sorted folder and a summary you did not write',
    caption: 'Completed Cowork run, subfolders created and summary paragraph visible', shot: 'cowork-completed-run.png',
    checks: [
      'Access was granted to one folder only',
      'You approved every step and read what each one did',
      'Nothing was deleted, files moved rather than lost',
      'The summary matches what is actually in the folder',
    ],
    footnote: 'Name one task from your own month that this would have handled. That is your Monday-morning job.',
  },
  {
    id: '55b', layout: 'exercise-example', module: 7,
    kicker: 'Module 07 · Optional practice', meta: '15-20 min · solo', title: 'Handing off a multi-step monthly report',
    scenario: 'Month-end is approaching, and your usual routine is: gather five site reports from a folder, pull out the key figures, draft a client-facing summary, then proofread it against the originals before sending, usually a two-hour job.',
    task: 'Instead of doing each step yourself in Chat, hand the whole sequence to Cowork in Manual mode, so you approve each stage but no longer do the manual reassembly.',
    examplePrompt: 'In this folder, read all five site reports. Extract the key figures and dates for each site. Draft a one-page client summary covering all five sites, grouped by site, with a short overall status line at the top. Then list any figure in your draft that you could not directly verify against the source reports.',
    expectedResult: 'A completed draft summary plus a short list of anything Claude could not verify, giving you a clear, short checklist to confirm before sending rather than starting the whole task from a blank page.',
    tryItYourself: 'Once you trust the output on a sample folder, try the same task again but ask it to also flag any site whose figures look unusually different from the other four, before you have even opened the individual reports yourself.',
  },
];
